/**
 * Integrating websocket with Redux, inspired by this article: https://www.taniarascia.com/websockets-in-redux/
 */
import { Middleware } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import flow from 'lodash/fp/flow';
import groupBy from 'lodash/fp/groupBy';
import mapValues from 'lodash/fp/mapValues';
import { addAppSuccessMessage } from 'modules/alerts/state';
import type { RootState } from 'store';

import * as actions from './actions';
import { Socket, SocketEvents, SocketMessage, SocketPayload } from './socket';
import { initSocketMessageDispatcher } from './socketMessageDispatcher';
import { captureSocketMessage } from './socketMessagesLogger';

const isThrottledMessage = ({ path, payload }: SocketMessage) =>
  (path.includes('ws_messages') || path.includes('throttled_messages')) && payload instanceof Array;

const getMessages = ({ path, payload }: SocketMessage) =>
  flow(
    groupBy<SocketPayload>('action' as keyof SocketPayload),
    mapValues<SocketPayload[], Omit<SocketPayload, 'action'>[]>((value) =>
      value.map((v) => {
        if (path.includes('ws_messages')) {
          return JSON.parse(v.params);
        }
        return v.params;
      })
    )
  )(payload);

export const socketMiddleware =
  (socket: Socket): Middleware<object, RootState> =>
  (params) => {
    const { dispatch, getState } = params;
    let disconnectTimestamp: number | null = null;

    socket.on(SocketEvents.connected, () => dispatch(actions.websocketConnected()));

    socket.on(SocketEvents.disconnected, () => {
      disconnectTimestamp = Date.now();
      logger.info('WebSocket disconnected', { wsBrokerURL: socket.brokerURL });
      dispatch(actions.websocketDisconnected());
    });

    socket.on(SocketEvents.reconnecting, () => {
      dispatch(actions.websocketReconnecting());
    });

    socket.on(SocketEvents.connectionError, () => {
      dispatch(actions.websocketConnectionError());
    });

    socket.on(SocketEvents.reconnected, () => {
      const reconnectDurationMs = disconnectTimestamp ? Date.now() - disconnectTimestamp : null;
      logger.info('WebSocket reconnected', { reconnectDurationMs, wsBrokerURL: socket.brokerURL });
      disconnectTimestamp = null;

      dispatch(actions.websocketConnected());
      dispatch(addAppSuccessMessage('Reconnected to server'));
      dispatch(actions.websocketCleanupAfterReconnect());
    });

    socket.on(SocketEvents.channelSubscribed, (channelName: string) => {
      dispatch(actions.addConnectedRoom(channelName));
    });

    socket.on(SocketEvents.channelUnsubscribed, (channelName: string) => {
      dispatch(actions.removeConnectedRoom(channelName));
    });

    const dispatchWsMessage = initSocketMessageDispatcher({ dispatch, getState });

    const handleWS = ({ path, payload }: SocketMessage) => {
      const messages = getMessages({ path, payload });

      Object.entries(messages).forEach(([key, value]) => {
        const action = `${path.split(':')[0]}:${key}`;

        value.forEach((v) => {
          dispatchWsMessage({
            path: action,
            payload: v,
          });
        });
      });
    };

    socket.on(SocketEvents.newMessage, (message: SocketMessage) => {
      if (isThrottledMessage(message)) {
        handleWS(message);
      } else {
        dispatchWsMessage(message);
      }
    });

    return (next) => (action: unknown) => {
      if (actions.websocketSubscribe.match(action)) {
        socket.subscribe(action.payload);
      } else if (actions.websocketUnsubscribe.match(action)) {
        socket.unsubscribe(action.payload);
      } else if (actions.websocketSend.match(action)) {
        captureSocketMessage({
          path: action.payload.path,
          payload: action.payload.data,
          type: 'OUTGOING',
        });
        socket.send(action.payload.path, action.payload.data, action.payload.plainBody);
      } else if (actions.websocketOpenPublicConnection.match(action)) {
        socket.openPublicConnection();
      } else if (actions.websocketClosePublicConnection.match(action)) {
        socket.closePublicConnection();
      }

      return next(action);
    };
  };
